import type { SVGProps } from 'react';

/**
 * Zestaw ikon SVG dla interfejsu mieszkańca — spójny, nowoczesny styl
 * (line, stroke 1.75, zaokrąglone). Zastępuje emoji, które renderowały się
 * różnie na różnych systemach.
 */

type IconProps = SVGProps<SVGSVGElement>;

const base = (props: IconProps) => ({
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  ...props,
});

// ── Akcje / UI ────────────────────────────────────────────────────────────────

export const IconSend = (p: IconProps) => (
  <svg {...base(p)}><path d="M22 2 11 13" /><path d="M22 2 15 22l-4-9-9-4Z" /></svg>
);
export const IconPin = (p: IconProps) => (
  <svg {...base(p)}><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></svg>
);
export const IconCheck = (p: IconProps) => (
  <svg {...base(p)}><path d="M20 6 9 17l-5-5" /></svg>
);
export const IconShield = (p: IconProps) => (
  <svg {...base(p)}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" /></svg>
);
export const IconBell = (p: IconProps) => (
  <svg {...base(p)}><path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>
);
export const IconCamera = (p: IconProps) => (
  <svg {...base(p)}><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3Z" /><circle cx="12" cy="13" r="3.5" /></svg>
);
export const IconSearch = (p: IconProps) => (
  <svg {...base(p)}><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
);
export const IconArrowRight = (p: IconProps) => (
  <svg {...base(p)}><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
);
export const IconArrowLeft = (p: IconProps) => (
  <svg {...base(p)}><path d="M19 12H5" /><path d="m12 19-7-7 7-7" /></svg>
);
export const IconWrench = (p: IconProps) => (
  <svg {...base(p)}><path d="M14.7 6.3a4 4 0 0 1-5.4 5.4L4 17v3h3l5.3-5.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.4-2.4 2.5-2.5Z" /></svg>
);
export const IconSparkles = (p: IconProps) => (
  <svg {...base(p)}><path d="M12 3 13.9 8.6 19.5 10.5 13.9 12.4 12 18 10.1 12.4 4.5 10.5 10.1 8.6 12 3Z" /><path d="M19 4v3M20.5 5.5h-3M5 17v2M6 18H4" /></svg>
);
export const IconClock = (p: IconProps) => (
  <svg {...base(p)}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
);
export const IconMail = (p: IconProps) => (
  <svg {...base(p)}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
);
export const IconLock = (p: IconProps) => (
  <svg {...base(p)}><rect x="4" y="10" width="16" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>
);
export const IconMap = (p: IconProps) => (
  <svg {...base(p)}><path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2Z" /><path d="M9 4v14M15 6v14" /></svg>
);
export const IconExternal = (p: IconProps) => (
  <svg {...base(p)}><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></svg>
);

// ── Kategorie zgłoszeń ──────────────────────────────────────────────────────────

// Ikony kategorii zgodne z zestawem marki (public/brand/icons)
export const IconPothole = (p: IconProps) => (
  <svg {...base(p)}><path d="M3 17.5c3-1 6-1.4 9-1.4s6 .4 9 1.4" /><path d="M6.5 17c.4-2.6 1.3-5.2 2.4-7.7" /><path d="M17.5 17c-.4-2.6-1.3-5.2-2.4-7.7" /><path d="M9.4 12.2c1.7.6 3.5.6 5.2 0" /></svg>
);
export const IconSidewalk = (p: IconProps) => (
  <svg {...base(p)}><rect x="3.5" y="4.5" width="17" height="15" rx="1.5" /><path d="M3.5 9.5h17M3.5 14.5h17" /><path d="M9 4.5v5M15 9.5v5M9 14.5v5" /></svg>
);
export const IconSign = (p: IconProps) => (
  <svg {...base(p)}><path d="M12 21v-7" /><path d="M12 4V3" /><rect x="4.5" y="6" width="15" height="8" rx="1.5" /><path d="M8 10h.01M11 10h5" /></svg>
);
export const IconStreetlight = (p: IconProps) => (
  <svg {...base(p)}><path d="M9.5 21h5" /><path d="M12 21v-9" /><path d="M8.5 12h7l-1-3.5h-5Z" /><path d="M12 8.5V5" /><path d="M12 5a2 2 0 0 1 2-2" /><path d="M9.6 15.5c.7 1.2 4.1 1.2 4.8 0" /></svg>
);
export const IconTree = (p: IconProps) => (
  <svg {...base(p)}><path d="M12 21v-6" /><path d="M12 15c-3.3 0-6-2-6-5.5C6 6 9 3 12 3s6 3 6 6.5C18 13 15.3 15 12 15Z" /><path d="M12 15l2.5-2.5M12 12 9.5 9.5" /></svg>
);
export const IconWaste = (p: IconProps) => (
  <svg {...base(p)}><path d="M4 7h16" /><path d="M6.5 7 7.5 20a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1L17.5 7" /><path d="M9.5 7V5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v2" /><path d="M10 11v6M14 11v6" /></svg>
);
export const IconWater = (p: IconProps) => (
  <svg {...base(p)}><path d="M12 3s6 6.3 6 10.5a6 6 0 1 1-12 0C6 9.3 12 3 12 3Z" /><path d="M9 13.5a3 3 0 0 0 3 3" /></svg>
);
export const IconTraffic = (p: IconProps) => (
  <svg {...base(p)}><rect x="8.5" y="2.5" width="7" height="19" rx="3.5" /><circle cx="12" cy="7" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="12" cy="17" r="1.4" /></svg>
);
export const IconSnow = (p: IconProps) => (
  <svg {...base(p)}><path d="M12 3v18M4.2 7.5l15.6 9M19.8 7.5 4.2 16.5" /><path d="M12 3l-2 2m2-2 2 2M12 21l-2-2m2 2 2-2" /></svg>
);
export const IconDots = (p: IconProps) => (
  <svg {...base(p)}><circle cx="5" cy="12" r="1.5" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" /><circle cx="19" cy="12" r="1.5" fill="currentColor" stroke="none" /></svg>
);

// Mapowanie kluczy kategorii z API → komponent ikony (zgodne z zestawem marki)
export const CATEGORY_ICONS: Record<string, (p: IconProps) => JSX.Element> = {
  pothole: IconPothole,
  road: IconPothole,
  sidewalk: IconSidewalk,
  sign: IconSign,
  streetlight: IconStreetlight,
  lighting: IconStreetlight,
  tree: IconTree,
  greenery: IconTree,
  waste: IconWaste,
  trash: IconWaste,
  water: IconWater,
  traffic: IconTraffic,
  snow: IconSnow,
  winter: IconSnow,
  more: IconDots,
};

export function CategoryIcon({ name, ...props }: { name: string | null } & Omit<IconProps, 'name'>) {
  const Cmp = CATEGORY_ICONS[name ?? 'more'] ?? IconDots;
  return <Cmp {...props} />;
}
